// Báo cáo CCO — call center 2 phòng (Telesales · CSKH), báo cáo tuần cho CCO / BGĐ: chỉ xem, xuất file số tổng hợp.
// Nhân viên hiển thị theo mã. Telesales cùng nguồn Dashboard Telesales; CSKH, báo giá, QA, mức trợ giá là số minh hoạ chờ API.
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button, Segmented, Select, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip as ChartTip, XAxis, YAxis } from 'recharts';
import {
  AlertTriangle, ArrowRight, BadgeDollarSign, CalendarCheck, Camera, Clock3, Download, FileText, Gauge, Hourglass, Info,
  MessageSquareWarning, PhoneCall, PhoneMissed, Receipt, Repeat, Scale, Smile, TrendingDown, TrendingUp, UserCheck,
} from 'lucide-react';
import Layout from '../../Layout';
import type { Mang } from '../Telesales/components/Types';
import { DAY, LEAD_DAYS, MANGS, buildModel } from '../Telesales/dashboard/data';
import { FUNNEL, pct, type Ctx } from '../Telesales/dashboard/logic';
import useTeleDash from '../Telesales/dashboard/store';
import { useTeleSettingsSync } from '../Telesales/settingsApi';
import { Bar, Card, Hint, Kpi, mmss, money, num } from '../Telesales/dashboard/ui';
import { allDeposits } from '../Telesales/lead/deposit';
import { useLeadStore } from '../Telesales/lead/store';
import {
  CS_STAGES, GAP, PENDING, QUALITY_SEC, RECALL_SOP, WEEK_BACK, agentRows, csKpi, notes, quoteKpi, teamOf, teleKpi, trend, weekOf,
  type AgentRow, type CsKpi, type Level, type Note, type QuoteKpi,
} from './report';

/* Màu 2 đường xu hướng — đã kiểm tra tách biệt cho người mù màu, nền sáng và tối */
const C_BOOK = '#1c8fb0';
const C_CLOSE = '#d97706';
const FUNNEL_COLORS = ['#a5d4e1', '#7cc3d6', '#56aec6', '#3e91ab', '#2c6b7f', '#1f5566'];
const SUBSIDY_COLORS = ['#2c6b7f', '#56aec6', '#a5d4e1'];

/* ---------- thành phần nhỏ ---------- */

/** So với tuần trước: xanh khi tốt lên, đỏ khi xấu đi */
function Delta({ cur, prev, unit = 'điểm', better = 'up', fmt = String }: { cur: number | null; prev: number | null; unit?: string; better?: 'up' | 'down'; fmt?: (n: number) => string }) {
  if (cur == null || prev == null) return null;
  const d = Math.round((cur - prev) * 10) / 10;
  if (!d) return <span className="block text-[11.5px] text-gray-400">Như tuần trước</span>;
  const good = (d > 0) === (better === 'up');
  const Icon = d > 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`mt-0.5 flex items-center gap-1 text-[11.5px] font-medium ${good ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
      <Icon size={13} />{d > 0 ? '+' : '−'}{fmt(Math.abs(d))} {unit} so với tuần trước
    </span>
  );
}
const Sub = ({ text, delta }: { text?: React.ReactNode; delta?: React.ReactNode }) => (
  <>
    {text && <span className="block">{text}</span>}
    {delta}
  </>
);

const SrcTag = ({ kind }: { kind: 'tele' | 'demo' | 'local' }) => (
  kind === 'tele' ? <Tooltip title="Cùng nguồn số liệu với Dashboard Telesales (hiện là dữ liệu minh hoạ, sẽ lấy từ tổng đài)"><Tag color="cyan" className="!m-0">Cùng nguồn Dashboard Telesales</Tag></Tooltip>
    : kind === 'local' ? <Tag color="green" className="!m-0">Dữ liệu trên máy</Tag>
      : <Tag color="gold" className="!m-0">Số minh hoạ — chờ nối API</Tag>
);

function Section({ id, no, title, desc, src, children }: { id: string; no: number; title: string; desc?: React.ReactNode; src: React.ReactNode; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-4 space-y-3 pt-2">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-gray-200 pb-2 dark:border-gray-700">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#3e91ab] text-[12px] font-bold text-white">{no}</span>
        <h2 className="text-[16px] font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        {src}
        {desc && <span className="basis-full text-[12.5px] text-gray-500 dark:text-gray-400 lg:basis-auto">{desc}</span>}
      </div>
      {children}
    </section>
  );
}

const KPI_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-3';

/** Tỷ lệ của nhân viên, tô màu khi lệch mức chung ≥ GAP điểm */
const Rate = ({ v, team, n }: { v: number; team: number; n: number }) => {
  if (!n) return <span className="text-gray-400">—</span>;
  const cls = v <= team - GAP ? 'font-semibold text-red-500' : v >= team + GAP ? 'font-semibold text-green-600 dark:text-green-400' : '';
  return <Tooltip title={`Mức chung ${team}%`}><span className={cls}>{v}%</span></Tooltip>;
};

const SubsidyCell = ({ v, closed }: { v: [number, number, number]; closed: number }) => (
  closed ? (
    <Tooltip title={`Không trợ giá ${v[0]}% · Mức 1 ${v[1]}% · Mức 2 ${v[2]}% trên ${closed} đơn chốt`}>
      <div className="w-[120px]">
        <div className="flex h-2 gap-[2px] overflow-hidden rounded-full">
          {v.map((x, i) => <span key={i} style={{ width: `${x}%`, background: SUBSIDY_COLORS[i] }} />)}
        </div>
        <div className="mt-0.5 text-[11px] tabular-nums text-gray-500">{v.join(' · ')}%</div>
      </div>
    </Tooltip>
  ) : <span className="text-gray-400">—</span>
);

const LEVEL: Record<Level, { icon: React.ReactNode; cls: string }> = {
  red: { icon: <AlertTriangle size={15} />, cls: 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400' },
  amber: { icon: <Clock3 size={15} />, cls: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400' },
  info: { icon: <Info size={15} />, cls: 'bg-[#e8f4f7] text-[#2c6b7f] dark:bg-[#3e91ab]/20 dark:text-[#7cc3d6]' },
};
const SECTION_ID: Record<Note['section'], string> = { tele: 'cco-tele', cs: 'cco-cs', quote: 'cco-quote' };
const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

/* ---------- xuất Excel số tổng hợp ---------- */
function exportXlsx(o: { label: string; file: string; tk: ReturnType<typeof teleKpi>; agents: AgentRow[]; cs: CsKpi; qk: QuoteKpi; checking: number; mang?: Mang }) {
  const { tk, cs, qk } = o;
  const wb = XLSX.utils.book_new();
  const kpi = (rows: [string, string | number][]) => XLSX.utils.aoa_to_sheet([['Kỳ', o.label], ['Mảng', o.mang ?? 'Tất cả'], [], ['Chỉ số', 'Giá trị'], ...rows]);
  XLSX.utils.book_append_sheet(wb, kpi([
    ...FUNNEL.map((f, i) => [f, tk.fn[i]] as [string, number]),
    ['Doanh số chốt (đ)', tk.revenue],
    ['Tốc độ gọi khách mới (phút, trung vị)', tk.firstMin ?? ''],
    ['% khách mới được gọi trong ngày', tk.sameDay],
    ['Số lần gọi / khách', tk.tries],
    ['Gọi đi', tk.out], ['Kết nối gọi đi (%)', pct(tk.outOk, tk.out)], [`% cuộc thực chất (> ${QUALITY_SEC}s)`, tk.quality],
    ['Gọi đến', tk.inbound], ['Gọi đến bị nhỡ (%)', pct(tk.missed, tk.inbound)], ['Service level (%)', tk.sl], ['ASA (giây)', tk.asa], ['AHT (giây)', tk.aht],
  ]), 'Telesales');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(o.agents.map(a => ({
    'Mã NV': a.id, 'Mảng': a.mang ?? 'Chưa phân luồng', 'Gọi đi': a.out, 'Kết nối (%)': a.connect, 'Cuộc thực chất (%)': a.quality,
    'Đặt lịch / kết nối (%)': a.book, 'Đến / lịch (%)': a.show, 'Chốt-cọc / đến (%)': a.close, 'Lịch đặt': a.booked, 'Chốt / cọc': a.closed,
    'Không trợ giá (%)': a.subsidy[0], 'Mức 1 (%)': a.subsidy[1], 'Mức 2 (%)': a.subsidy[2], 'Điểm QA': a.qa ?? '', 'Hẹn gọi lại quá hạn': a.overdue,
  }))), 'NV Telesales');
  XLSX.utils.book_append_sheet(wb, kpi([
    ['T+24h đúng hạn (%)', cs.t24], ['CSAT', cs.csat], ['Phiếu CSAT', cs.csatN], ['Khiếu nại đúng hạn', `${cs.complaintsOnTime}/${cs.complaints}`],
    ['Khách quay lại 90 ngày (%)', cs.returnRate], ['Doanh thu khách cũ qua CSKH (đ)', cs.revenue], ['Gọi lại khách cũ A, C', cs.recallAC],
    ['Mốc chăm sóc có ảnh (%)', cs.photo], ['CS5 → quay lại', `${cs.cs5Back}/${cs.cs5}`],
    ...CS_STAGES.map((s, i) => [`${s.code} · ${s.label}`, cs.dist[i]] as [string, number]),
  ]), 'CSKH (minh hoa)');
  XLSX.utils.book_append_sheet(wb, kpi([
    ['Báo giá', qk.quotes], ['Báo giá → đặt lịch', qk.quoteBooked], ['Báo giá hết hạn chưa phản hồi', qk.expired],
    ['Chênh lệch giá online và giá chốt (%)', qk.priceGap], ['Cọc online chờ đối soát', o.checking],
    ...qk.channels.map(c => [`Kênh ${c.channel}: lượt chạm / trả lời % / đặt lịch`, `${c.touches} / ${c.reply}% / ${c.booked}`] as [string, string]),
  ]), 'Bao gia - kenh');
  XLSX.writeFile(wb, o.file);
}

export default function BaoCaoCCOPage() {
  useTeleSettingsSync();
  const { cfg, appt, done, assign, seed, ensureSeed } = useTeleDash();
  const byId = useLeadStore(s => s.byId);
  const [now] = useState(() => Date.now());
  // CCO xem tuần đã khép lại là chính; vẫn chọn được tuần đang chạy
  const [back, setBack] = useState(1);
  const [mangKey, setMangKey] = useState<string>('all');
  const mang = mangKey === 'all' ? undefined : (mangKey as Mang);

  useEffect(() => { ensureSeed(); }, [ensureSeed]);
  const dateKey = dayjs(now).format('YYYY-MM-DD');
  const m = useMemo(() => buildModel(dateKey), [dateKey]);
  const seedAt = seed.date === dateKey ? seed.at : now;
  const ctx: Ctx = useMemo(() => ({ m, now, cfg, ov: { appt, done, assign }, seedAt }), [m, now, cfg, appt, done, assign, seedAt]);

  const w = weekOf(now, back);
  const pw = weekOf(now, back + 1);
  const tk = useMemo(() => teleKpi(ctx, w, mang), [ctx, back, mang]); // eslint-disable-line react-hooks/exhaustive-deps
  const ptk = useMemo(() => teleKpi(ctx, pw, mang), [ctx, back, mang]); // eslint-disable-line react-hooks/exhaustive-deps
  const agents = useMemo(() => agentRows(ctx, w, w.key, mang), [ctx, back, mang]); // eslint-disable-line react-hooks/exhaustive-deps
  const team = teamOf(agents);
  const tr = useMemo(() => {
    const oldest = m.dayStart - LEAD_DAYS * DAY;
    const weeks = [5, 4, 3, 2, 1, 0].map(i => weekOf(now, back + i)).filter(x => x.from >= oldest);
    return trend(ctx, weeks, mang);
  }, [ctx, m, now, back, mang]);
  const cs = csKpi(w.key);
  const pcs = csKpi(pw.key);
  const qk = quoteKpi(ctx, w, tk, mang);
  const pqk = quoteKpi(ctx, pw, ptk, mang);
  const deps = allDeposits(byId);
  const checking = deps.filter(d => d.deposit.status === 'checking').length;
  const list = notes({ cfg, tk, agents, cs, checking, mang });

  const weekOptions = Array.from({ length: WEEK_BACK }, (_, i) => {
    const x = weekOf(now, i);
    return { value: i, label: x.running ? `${x.label} · đang chạy` : x.label };
  });
  const outRate = pct(tk.outOk, tk.out);
  const missRate = pct(tk.missed, tk.inbound);

  /* ---------- bảng nhân viên Telesales ---------- */
  const agentCols: ColumnsType<AgentRow> = [
    {
      title: 'Mã NV', key: 'id', fixed: 'left', width: 120,
      render: (_, a) => (
        <div className="leading-tight">
          <div className="font-semibold text-gray-800 dark:text-gray-100">{a.id}</div>
          {a.mang ? <span className="text-[11.5px] text-gray-500">{a.mang}</span>
            : <Tooltip title="Nhận khách tràn của mọi mảng — cần phân luồng để so sánh công bằng"><span className="text-[11.5px] font-medium text-amber-600 dark:text-amber-400">Chưa phân luồng</span></Tooltip>}
        </div>
      ),
    },
    { title: 'Gọi đi', dataIndex: 'out', align: 'right', sorter: (a, b) => a.out - b.out, render: num },
    { title: 'Kết nối', key: 'cn', align: 'right', sorter: (a, b) => a.connect - b.connect, render: (_, a) => <Rate v={a.connect} team={outRate} n={a.out} /> },
    { title: <span className="inline-flex items-center gap-1">Cuộc thực chất <Hint text={`Cuộc gọi đi kết nối, đàm thoại trên ${QUALITY_SEC} giây`} /></span>, key: 'q', align: 'right', sorter: (a, b) => a.quality - b.quality, render: (_, a) => <Rate v={a.quality} team={tk.quality} n={a.out} /> },
    { title: 'Đặt lịch / kết nối', key: 'bk', align: 'right', sorter: (a, b) => a.book - b.book, render: (_, a) => <Rate v={a.book} team={team.book} n={a.conn} /> },
    { title: <span className="inline-flex items-center gap-1">Đến / lịch <Hint text="Khách đến / lịch đã tới giờ hẹn (không tính lịch huỷ)" /></span>, key: 'sh', align: 'right', sorter: (a, b) => a.show - b.show, render: (_, a) => <Rate v={a.show} team={team.show} n={a.due} /> },
    { title: 'Chốt-cọc / đến', key: 'cl', align: 'right', sorter: (a, b) => a.close - b.close, render: (_, a) => <Rate v={a.close} team={team.close} n={a.arrived} /> },
    {
      title: <span className="inline-flex items-center gap-1">Đơn chốt theo mức trợ giá <Hint text="Không trợ giá · Mức 1 · Mức 2. Tách tác động của khung giá phân quyền khỏi năng lực tư vấn. Số minh hoạ — chờ chốt khung trợ giá." /></span>,
      key: 'sub', render: (_, a) => <SubsidyCell v={a.subsidy} closed={a.closed} />,
    },
    { title: <span className="inline-flex items-center gap-1">Điểm QA <Hint text="Số minh hoạ — chờ phiếu chấm QA" /></span>, key: 'qa', align: 'right', sorter: (a, b) => (a.qa ?? 0) - (b.qa ?? 0), render: (_, a) => (a.qa == null ? <Tooltip title="Chưa chấm"><span className="text-gray-400">—</span></Tooltip> : <span className={a.qa < 75 ? 'font-semibold text-amber-600' : ''}>{a.qa}</span>) },
    { title: <span className="inline-flex items-center gap-1">Hẹn gọi lại quá hạn <Hint text="Tính tại thời điểm xem báo cáo" /></span>, dataIndex: 'overdue', align: 'right', sorter: (a, b) => a.overdue - b.overdue, render: v => <span className={v ? 'font-semibold text-red-500' : 'text-gray-400'}>{v}</span> },
  ];

  const csStaffCols: ColumnsType<CsKpi['staff'][number]> = [
    { title: 'Mã NV', dataIndex: 'id', render: v => <b className="text-gray-800 dark:text-gray-100">{v}</b> },
    { title: 'Cuộc gọi', dataIndex: 'calls', align: 'right' },
    { title: 'T+24h đúng hạn', dataIndex: 't24', align: 'right', render: v => <span className={v < 90 ? 'font-semibold text-red-500' : ''}>{v}%</span> },
    { title: 'CSAT', dataIndex: 'csat', align: 'right', render: v => v.toLocaleString('vi-VN', { minimumFractionDigits: 1 }) },
    { title: 'Khiếu nại xử lý', key: 'kn', align: 'right', render: (_, s) => <span className={s.done < s.total ? 'font-semibold text-red-500' : ''}>{s.done} / {s.total}</span> },
    { title: 'Đơn CS-06', dataIndex: 'orders', align: 'right' },
    { title: 'Doanh thu', dataIndex: 'revenue', align: 'right', render: v => <b>{money(v)}</b> },
  ];
  const recallCols: ColumnsType<CsKpi['recall'][number]> = [
    { title: 'Phòng', dataIndex: 'dept', render: v => <Tag color={v === 'CSKH' ? 'green' : 'cyan'} className="!m-0">{v}</Tag> },
    { title: 'Nhóm', dataIndex: 'group', align: 'center', render: v => <b>{v}</b> },
    { title: 'Mô tả', dataIndex: 'desc' },
    { title: 'Kênh', dataIndex: 'channel', className: 'whitespace-nowrap' },
    { title: 'Đã chạm', dataIndex: 'touched', align: 'right' },
    { title: 'Ra lịch', dataIndex: 'booked', align: 'right', render: (v, r) => <span>{v}<span className="text-gray-400"> ({pct(v, r.touched)}%)</span></span> },
  ];
  const channelCols: ColumnsType<QuoteKpi['channels'][number]> = [
    { title: 'Kênh', dataIndex: 'channel', render: (v, r) => <span className="inline-flex items-center gap-1.5 font-semibold text-gray-800 dark:text-gray-100">{v}{r.real && <Tooltip title="Cùng nguồn Dashboard Telesales"><span className="h-1.5 w-1.5 rounded-full bg-[#3e91ab]" /></Tooltip>}</span> },
    { title: 'Lượt chạm', dataIndex: 'touches', align: 'right', render: num },
    { title: 'Khách trả lời', key: 'r', align: 'right', render: (_, r) => `${r.reply}%${r.real ? ' (kết nối)' : ''}` },
    { title: 'Đặt lịch', dataIndex: 'booked', align: 'right' },
    { title: <span className="inline-flex items-center gap-1">Phản hồi tin (trung vị) <Hint text="Chuẩn phản hồi tin ≤ 5 phút trong ca (R25)" /></span>, dataIndex: 'replyMin', align: 'right', render: v => (v == null ? <span className="text-gray-400">—</span> : <span className={v > 5 ? 'font-semibold text-red-500' : ''}>{v} phút</span>) },
  ];
  const overrideCols: ColumnsType<QuoteKpi['overrides'][number]> = [
    { title: 'Mã NV', dataIndex: 'id', render: v => <b className="text-gray-800 dark:text-gray-100">{v}</b> },
    { title: 'Báo giá', dataIndex: 'quotes', align: 'right' },
    { title: 'Xin duyệt vượt quyền', dataIndex: 'requested', align: 'right', render: (v, r) => <span className={pct(v, r.quotes) >= 20 ? 'font-semibold text-amber-600' : ''}>{v}<span className="text-gray-400"> ({pct(v, r.quotes)}%)</span></span> },
    { title: 'Được duyệt', dataIndex: 'approved', align: 'right', render: (v, r) => (r.requested ? `${v} / ${r.requested}` : <span className="text-gray-400">—</span>) },
  ];

  return (
    <Layout>
      <Helmet>
        <title>Báo cáo CCO | Khothi</title>
      </Helmet>

      <div className="mx-auto w-full max-w-[1800px] space-y-4 pb-16">
        {/* Tiêu đề + kỳ */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Báo cáo CCO</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Call center 2 phòng Telesales · CSKH — báo cáo tuần cho CCO, BGĐ. Chỉ xem, nhân viên hiển thị theo mã.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={back} onChange={setBack} options={weekOptions} className="min-w-[250px]" />
            <Button icon={<Download size={15} />} onClick={() => exportXlsx({ label: w.label, file: `bao-cao-cco-${w.key}${mang ? `-${mang}` : ''}.xlsx`, tk, agents, cs, qk, checking, mang })}>Xuất Excel</Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* Màn hẹp: cuộn ngang thay vì tràn khỏi trang */}
          <div className="max-w-full overflow-x-auto [scrollbar-width:none]">
            <Segmented value={mangKey} onChange={v => setMangKey(String(v))} options={[{ value: 'all', label: 'Tất cả mảng' }, ...MANGS.map(x => ({ value: x, label: x }))]} />
          </div>
          <span className="text-[12.5px] text-gray-500 dark:text-gray-400">
            So sánh với {pw.label}{w.running && <> · <b className="text-amber-600 dark:text-amber-400">tuần đang chạy, số tính đến {dayjs(now).format('HH:mm DD/MM')}</b></>}
          </span>
        </div>

        {/* Điểm cần chú ý */}
        <Card
          title={<span className="inline-flex items-center gap-2"><MessageSquareWarning size={16} className="text-[#3e91ab]" />Điểm cần chú ý trong kỳ</span>}
          extra={(
            <div className="flex flex-wrap gap-1.5">
              {([['cco-tele', '1. Telesales'], ['cco-cs', '2. CSKH'], ['cco-quote', '3. Báo giá · cọc · kênh'], ['cco-pending', 'Chờ chốt']] as const).map(([id, label]) => (
                <Button key={id} size="small" onClick={() => jump(id)}>{label}</Button>
              ))}
            </div>
          )}
        >
          {list.length ? (
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              {list.map((n, i) => (
                <button key={i} type="button" onClick={() => jump(SECTION_ID[n.section])} className="group flex items-start gap-2.5 rounded-lg border border-gray-100 px-3 py-2 text-left text-[13px] text-gray-700 transition hover:border-[#3e91ab]/50 dark:border-gray-700 dark:text-gray-200">
                  <span className={`mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${LEVEL[n.level].cls}`}>{LEVEL[n.level].icon}</span>
                  <span className="min-w-0 flex-1 pt-0.5">{n.text}</span>
                  <ArrowRight size={14} className="mt-1 shrink-0 text-gray-300 group-hover:text-[#3e91ab]" />
                </button>
              ))}
            </div>
          ) : <div className="py-3 text-center text-[13px] text-gray-400">Không có điểm bất thường trong kỳ.</div>}
        </Card>

        {/* 1. Telesales */}
        <Section id="cco-tele" no={1} title="Phòng Telesales" src={<SrcTag kind="tele" />} desc={mang ? `Mảng ${mang} — nhân viên đúng mảng và nhân viên chưa phân luồng` : 'Tất cả mảng'}>
          <div className={KPI_GRID}>
            <Kpi icon={<Clock3 size={16} />} tone={tk.firstMin != null && tk.firstMin > cfg.newLeadMin ? 'red' : 'teal'} alert={tk.firstMin != null && tk.firstMin > cfg.newLeadMin}
              label="Tốc độ gọi khách mới" value={tk.firstMin == null ? '—' : `${tk.firstMin} phút`}
              hint="Trung vị số phút từ lúc khách vào hệ thống đến cuộc gọi đầu tiên (không tính khách Hotline tự gọi)"
              sub={<Sub text={`Chuẩn ≤ ${cfg.newLeadMin} phút`} delta={<Delta cur={tk.firstMin} prev={ptk.firstMin} unit="phút" better="down" />} />} />
            <Kpi icon={<PhoneCall size={16} />} label="Gọi trong ngày" value={`${tk.sameDay}%`} hint="% khách mới (không tính Hotline) được gọi ngay trong ngày vào hệ thống"
              sub={<Sub text={`Trung bình ${tk.tries.toLocaleString('vi-VN')} lần gọi / khách`} delta={<Delta cur={tk.sameDay} prev={ptk.sameDay} />} />} />
            <Kpi icon={<UserCheck size={16} />} tone="sky" label="Kết nối gọi đi" value={`${outRate}%`}
              sub={<Sub text={`${num(tk.outOk)} / ${num(tk.out)} cuộc`} delta={<Delta cur={outRate} prev={pct(ptk.outOk, ptk.out)} />} />} />
            <Kpi icon={<FileText size={16} />} tone="indigo" label="Cuộc thực chất" value={`${tk.quality}%`} hint={`Cuộc gọi đi kết nối có đàm thoại trên ${QUALITY_SEC} giây — ngưỡng đang chờ chốt`}
              sub={<Sub text={`Đàm thoại > ${QUALITY_SEC} giây`} delta={<Delta cur={tk.quality} prev={ptk.quality} />} />} />
            <Kpi icon={<Gauge size={16} />} tone={tk.sl >= 80 ? 'green' : 'amber'} label="Service level" value={tk.inbound ? `${tk.sl}%` : '—'}
              hint={`Tỷ lệ cuộc gọi đến được nhận trong ${cfg.slaSec} giây`}
              sub={<Sub text={`ASA ${mmss(tk.asa)} · AHT ${mmss(tk.aht)}`} delta={<Delta cur={tk.sl} prev={ptk.sl} />} />} />
            <Kpi icon={<PhoneMissed size={16} />} tone={missRate > 10 ? 'red' : 'slate'} label="Gọi đến bị nhỡ" value={tk.inbound ? `${missRate}%` : '—'}
              sub={<Sub text={`${num(tk.missed)} / ${num(tk.inbound)} cuộc gọi đến`} delta={<Delta cur={missRate} prev={pct(ptk.missed, ptk.inbound)} better="down" />} />} />
          </div>

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
            <Card className="xl:col-span-5" title={<span className="inline-flex items-center gap-1.5">Phễu S0 → chốt <Hint text="Theo nhóm khách vào trong tuần; tỷ lệ là chuyển đổi so với bước liền trước" /></span>}>
              <div className="space-y-2.5">
                {FUNNEL.map((f, i) => {
                  const v = tk.fn[i];
                  return (
                    <div key={f} className="grid grid-cols-[88px_1fr_auto] items-center gap-2.5 text-[13px]">
                      <span className="text-gray-600 dark:text-gray-300">{f}</span>
                      <div className="h-6 overflow-hidden rounded-md bg-gray-50 dark:bg-gray-700/40">
                        <div className="flex h-full items-center rounded-md px-2 text-[12px] font-semibold text-white transition-all" style={{ width: `${Math.max(6, pct(v, tk.fn[0]))}%`, background: FUNNEL_COLORS[i] }}>{num(v)}</div>
                      </div>
                      <span className="w-10 text-right text-[12px] tabular-nums text-gray-500">{i ? `${pct(v, tk.fn[i - 1])}%` : ''}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 border-t border-gray-100 pt-3 dark:border-gray-700">
                <div>
                  <div className="text-[12px] text-gray-500">Khách mới → chốt / cọc</div>
                  <div className="text-[20px] font-light text-slate-800 dark:text-white">{pct(tk.fn[5], tk.fn[0])}%</div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-[12px] text-gray-500">Doanh số chốt <Hint text="Theo kết quả tư vấn tại cơ sở. Khi liên kết CMS ↔ POS sẽ lấy doanh thu thực từ POS." /></div>
                  <div className="text-[20px] font-light text-slate-800 dark:text-white">{money(tk.revenue)}</div>
                </div>
              </div>
            </Card>

            <Card className="xl:col-span-7" title={`Xu hướng ${tr.length} tuần — ${mang ?? 'tất cả mảng'}`}>
              <div className="h-[250px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={tr} margin={{ top: 8, right: 16, left: -12, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="rgba(148,163,184,0.25)" />
                    <XAxis dataKey="week" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={v => `${v}%`} domain={[0, 'auto']} />
                    <ChartTip
                      formatter={(v: number, name: string) => [`${v}%`, name]}
                      labelFormatter={(_, p) => { const x = p?.[0]?.payload; return x ? `${x.label}${x.running ? ' · đang chạy' : ''}` : ''; }}
                      contentStyle={{ borderRadius: 8, fontSize: 12 }}
                    />
                    <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="book" name="Đặt lịch / kết nối" stroke={C_BOOK} strokeWidth={2} dot={{ r: 4, strokeWidth: 2, fill: '#fff', strokeDasharray: '0' }} activeDot={{ r: 5, strokeDasharray: '0' }} />
                    <Line type="monotone" dataKey="close" name="Chốt-cọc / đến" stroke={C_CLOSE} strokeWidth={2} strokeDasharray="6 4" dot={{ r: 4, strokeWidth: 2, fill: '#fff', strokeDasharray: '0' }} activeDot={{ r: 5, strokeDasharray: '0' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-1 text-[12px] text-gray-500">Tuần gần nhất còn lịch hẹn chưa tới ngày nên tỷ lệ đến / chốt thường thấp hơn thực tế khi tuần khép lại.</p>
            </Card>
          </div>

          <Card title="Nhân viên Telesales theo mã" extra={<span className="text-[12px] text-gray-500"><span className="font-semibold text-red-500">Đỏ</span> / <span className="font-semibold text-green-600">xanh</span>: thấp / cao hơn mức chung ≥ {GAP} điểm</span>}>
            <Table
              rowKey="id" size="small" pagination={false} columns={agentCols} dataSource={agents} scroll={{ x: 'max-content' }}
              summary={() => (
                <Table.Summary.Row className="bg-gray-50 font-semibold dark:bg-gray-700/40 [&>td]:![font-family:inherit]">
                  <Table.Summary.Cell index={0}>Mức chung</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">{num(team.out)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">{outRate}%</Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">{tk.quality}%</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">{team.book}%</Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">{team.show}%</Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">{team.close}%</Table.Summary.Cell>
                  <Table.Summary.Cell index={7}><span className="font-normal text-gray-500">{team.closed} đơn chốt</span></Table.Summary.Cell>
                  <Table.Summary.Cell index={8} />
                  <Table.Summary.Cell index={9} align="right"><span className={team.overdue ? 'text-red-500' : ''}>{team.overdue}</span></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Section>

        {/* 2. CSKH */}
        <Section id="cco-cs" no={2} title="Phòng CSKH" src={<SrcTag kind="demo" />} desc={mang ? 'CSKH chưa tách theo mảng — số của cả phòng' : undefined}>
          <div className={KPI_GRID}>
            <Kpi icon={<CalendarCheck size={16} />} tone={cs.t24 < 100 ? 'amber' : 'green'} label="T+24h đúng hạn" value={`${cs.t24}%`}
              sub={<Sub text="Chuẩn SOP 100%" delta={<Delta cur={cs.t24} prev={pcs.t24} />} />} />
            <Kpi icon={<Smile size={16} />} tone="green" label="CSAT trung bình" value={cs.csat.toLocaleString('vi-VN', { minimumFractionDigits: 1 })}
              sub={<Sub text={`${cs.csatN} phiếu · ${cs.csatLow} phiếu ≤ 3 điểm`} delta={<Delta cur={cs.csat} prev={pcs.csat} unit="" fmt={n => n.toLocaleString('vi-VN')} />} />} />
            <Kpi icon={<MessageSquareWarning size={16} />} tone={cs.complaintsOnTime < cs.complaints ? 'red' : 'teal'} alert={cs.complaintsOnTime < cs.complaints}
              label="Khiếu nại đúng hạn" value={`${cs.complaintsOnTime} / ${cs.complaints}`} hint="Hạn xử lý khiếu nại đang chờ chốt"
              sub={<Sub text={cs.complaintsOnTime < cs.complaints ? `${cs.complaints - cs.complaintsOnTime} khiếu nại trễ hạn` : 'Không có khiếu nại trễ'} />} />
            <Kpi icon={<Repeat size={16} />} tone="sky" label="Khách quay lại 90 ngày" value={`${cs.returnRate}%`}
              sub={<Sub delta={<Delta cur={cs.returnRate} prev={pcs.returnRate} />} />} />
            <Kpi icon={<BadgeDollarSign size={16} />} label="Doanh thu khách cũ" value={money(cs.revenue)} hint="R23 — doanh thu khách đã dùng dịch vụ quay lại mua qua CSKH"
              sub={<Sub delta={<Delta cur={cs.revenue / 1e6} prev={pcs.revenue / 1e6} unit="tr" />} />} />
            <Kpi icon={<PhoneCall size={16} />} tone={cs.recallAC < RECALL_SOP ? 'amber' : 'teal'} label="Gọi lại khách cũ A, C" value={cs.recallAC}
              sub={<Sub text={`Chuẩn SOP ≥ ${RECALL_SOP} / tuần`} delta={<Delta cur={cs.recallAC} prev={pcs.recallAC} unit="lượt" />} />} />
            <Kpi icon={<Camera size={16} />} tone="indigo" label="Mốc chăm sóc có ảnh" value={`${cs.photo}%`} hint="Các mốc T+24h → T+90 đến hạn trong tuần có ảnh trước / sau"
              sub={<Sub delta={<Delta cur={cs.photo} prev={pcs.photo} />} />} />
            <Kpi icon={<Hourglass size={16} />} tone="amber" label="CS5 → quay lại" value={`${pct(cs.cs5Back, cs.cs5)}%`} hint="Ngưỡng xếp CS5 (lâu chưa quay lại) đang chờ chốt"
              sub={<Sub text={`${cs.cs5Back} / ${num(cs.cs5)} khách CS5`} delta={<Delta cur={pct(cs.cs5Back, cs.cs5)} prev={pct(pcs.cs5Back, pcs.cs5)} />} />} />
          </div>

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
            <Card className="xl:col-span-5" title="Phân bố khách theo CS0–CS6 (cuối kỳ)">
              <div className="space-y-2.5">
                {CS_STAGES.map((s, i) => {
                  const total = cs.dist.reduce((a, b) => a + b, 0);
                  return (
                    <div key={s.code} className="text-[13px]">
                      <div className="mb-1 flex items-baseline justify-between gap-2">
                        <span className="min-w-0 truncate text-gray-700 dark:text-gray-200"><b>{s.code}</b> · {s.label}</span>
                        <span className="shrink-0 tabular-nums text-gray-500">{num(cs.dist[i])} <span className="text-gray-400">({pct(cs.dist[i], total)}%)</span></span>
                      </div>
                      <Bar value={cs.dist[i]} max={Math.max(...cs.dist)} color={s.code === 'CS5' ? '#d97706' : undefined} />
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-[12px] text-gray-500">Tổng {num(cs.dist.reduce((a, b) => a + b, 0))} khách. CS5 chiếm phần lớn → ưu tiên kịch bản gọi lại nhóm A.</p>
            </Card>
            <div className="flex min-w-0 flex-col gap-3 xl:col-span-7">
              <Card title="Nhân viên CSKH theo mã">
                <Table rowKey="id" size="small" pagination={false} columns={csStaffCols} dataSource={cs.staff} scroll={{ x: 'max-content' }} />
              </Card>
              <Card title="Gọi lại theo nhóm SOP — tách 2 phòng">
                <Table rowKey={r => `${r.dept}-${r.group}`} size="small" pagination={false} columns={recallCols} dataSource={cs.recall} scroll={{ x: 'max-content' }} />
                <p className="mt-2 text-[12px] text-gray-500">
                  CSKH {cs.recallAC} lượt (chuẩn SOP ≥ {RECALL_SOP} / tuần cho nhóm A, C) · Telesales {cs.recall.filter(r => r.dept === 'Telesales').reduce((s, r) => s + r.touched, 0)} lượt (R38).
                </p>
              </Card>
            </div>
          </div>
        </Section>

        {/* 3. Báo giá, cọc, kênh */}
        <Section id="cco-quote" no={3} title="Báo giá, cọc và kênh nhắn tin" src={<SrcTag kind="demo" />} desc="Dòng Gọi điện cùng nguồn Dashboard Telesales · Cọc chờ đối soát lấy từ màn Đối soát cọc">
          <div className={KPI_GRID}>
            <Kpi icon={<Receipt size={16} />} label="Báo giá → đặt lịch" value={`${pct(qk.quoteBooked, qk.quotes)}%`}
              sub={<Sub text={`${qk.quoteBooked} / ${qk.quotes} báo giá tham khảo`} delta={<Delta cur={pct(qk.quoteBooked, qk.quotes)} prev={pct(pqk.quoteBooked, pqk.quotes)} />} />} />
            <Kpi icon={<Hourglass size={16} />} tone="amber" label="Báo giá hết hạn" value={qk.expired} hint="R18 — báo giá hết hạn mà khách chưa phản hồi; hệ thống gợi ý nhắc ưu đãi"
              sub={<Sub text="Khách chưa phản hồi" delta={<Delta cur={qk.expired} prev={pqk.expired} unit="báo giá" better="down" />} />} />
            <Kpi icon={<Scale size={16} />} tone="indigo" label="Chênh giá online / chốt" value={`+${qk.priceGap}%`} hint="R33 — trung vị trên các đơn đã nối phiếu báo giá chính thức"
              sub={<Sub text={`Trung vị · ${qk.gapOrders} đơn đã nối phiếu`} delta={<Delta cur={qk.priceGap} prev={pqk.priceGap} better="down" />} />} />
            <Kpi icon={<Receipt size={16} />} tone={checking ? 'amber' : 'green'} label="Cọc chờ đối soát" value={checking} hint="R32 — Kế toán khớp sao kê trước khi xác nhận đã nhận cọc. Số thật đang lưu trên máy."
              sub={<Link to="/telesales/deposits" className="inline-flex items-center gap-1 text-[#3e91ab] hover:underline">Mở Đối soát cọc <ArrowRight size={12} /></Link>} />
          </div>
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <Card title="Theo kênh">
              <Table rowKey="channel" size="small" pagination={false} columns={channelCols} dataSource={qk.channels} scroll={{ x: 'max-content' }} />
              <p className="mt-2 text-[12px] text-gray-500">Tốc độ liên hệ khách mới tính lần chạm đầu tiên ở bất kỳ kênh nào.</p>
            </Card>
            <Card title="Xin duyệt vượt quyền theo mã nhân viên">
              <Table rowKey="id" size="small" pagination={false} columns={overrideCols} dataSource={qk.overrides} scroll={{ x: 'max-content' }} />
              <p className="mt-2 text-[12px] text-gray-500">Đọc cùng cột “Đơn chốt theo mức trợ giá” ở bảng nhân viên Telesales. Xin duyệt ≥ 20% số báo giá được tô cam.</p>
            </Card>
          </div>
        </Section>

        {/* Chờ chốt */}
        <section id="cco-pending" className="scroll-mt-4 pt-2">
          <Card title={<span className="inline-flex items-center gap-2"><Info size={16} className="text-amber-500" />Chờ chốt để số liệu thành chính thức</span>}>
            <Table
              rowKey="item" size="small" pagination={false} dataSource={PENDING} scroll={{ x: 'max-content' }}
              columns={[
                { title: 'Hạng mục', dataIndex: 'item', render: v => <b className="text-gray-800 dark:text-gray-100">{v}</b> },
                { title: 'Đang tạm dùng', dataIndex: 'now' },
                { title: 'Ảnh hưởng tới', dataIndex: 'affects', render: v => <span className="text-gray-500">{v}</span> },
              ]}
            />
          </Card>
        </section>
      </div>
    </Layout>
  );
}
